import { useState } from "react";
import router, { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { Table } from "common/ui/Table";
import { Stack } from "common/ui/Stack";
import { Paragraph } from "common/ui/Paragraph";
import { Button } from "common/ui/Button";
import { Icon } from "common/ui/Icon";
import { Toast } from "common/ui/Toast";
import { ListingStatusPill } from "common/elements/ListingStatusPill";

import {
  currencyFormatLocale,
  formatDateMonthYear,
  getNumOfDaysFromDate
} from "common/utils/utils";

import { DraftStatus, ListingLookupDto, ListingTier } from "common/middleware/api.services";
import { createDraftOfActiveListing } from "common/api/createDraftOfActiveListing";
import { getProperty } from "common/middleware/api";
import { toEnumValue } from "common/utils/toEnumValue";
import { APP_ROUTES } from "common/constants/routes";

interface PublishedListingProps {
  listings: ListingLookupDto[],
  setListingLink: (value: string) => void,
}

export function PublishedListingsTable({
  listings,
  setListingLink,
}: PublishedListingProps) {
  const { locale } = useRouter();
  const { t } = useTranslation("admin");

  const [draftError, setDraftError] = useState(false);
  const [isRedirectingToDraft, setIsRedirectingToDraft] = useState(false);

  const checkDraftAndRedirect = async (listing: ListingLookupDto) => {
    setIsRedirectingToDraft(true);

    try {
      if ([DraftStatus.None, DraftStatus.LastUpdated].includes(toEnumValue(DraftStatus, listing.draftStatus))) {
        const property = await getProperty(undefined, listing.id.toString());
        await createDraftOfActiveListing(property);
      }

      const isStandardListing = toEnumValue(ListingTier, listing.tier) === ListingTier.Standard;
      const isDraftDeclined = toEnumValue(DraftStatus, listing.draftStatus) === DraftStatus.DraftDeclined;

      router.push({
        pathname: (isStandardListing || isDraftDeclined) ? "/listings/review" : "/listings/property",
        query: {
          propertyId: listing.id,
          status: "draft"
        }
      });
    } catch (error) {
      setDraftError(true);
    }

    setIsRedirectingToDraft(false);
  }

  const rows = listings?.map((listing) => {
    const listingState = listing.state.toString();
    const draftStatus = listing?.draftStatus?.toString();
    const isCurveballListing = toEnumValue(ListingTier, listing.tier) === ListingTier.Curveball;
    const isStandardListing = toEnumValue(ListingTier, listing.tier) === ListingTier.Standard;
    const isEditable = !["sold", "reserved"].includes(listingState)
      && draftStatus !== "pendingReview"
      && !isCurveballListing;
    const subscriptionRenewalDate = listing.subscriptionRenewalDateTimeUtc;

    const address = (
      <>
        <Paragraph variant="p1">
          {listing.address.unitNumber}
          {" "}
          {listing.address.street}
        </Paragraph>
        <Paragraph variant="p1">
          {listing.address.city}
          {" "}
          {listing.address.province}
          {" "}
          {listing.address.postalCode}
        </Paragraph>
        <Paragraph variant="p1">
          {listing.address.country}
        </Paragraph>
      </>
    );

    const listPrice = (
      <Paragraph variant="p1">
        {currencyFormatLocale(listing.price, locale)}
      </Paragraph>
    );

    const status = (
      <ListingStatusPill status={listingState} />
    );

    const startDate = (
      <Stack direction="column" gap={4}>
        <Paragraph variant="p1">
          {listingState === "active" ? (
            formatDateMonthYear(locale, listing.activeDateTimeUtc)
          ) : (
            "-"
          )}
        </Paragraph>
        {(draftStatus !== "none" && listing.draftStatusDate) && (
          <Paragraph variant="p2">
            {t(`publishedListingsTable.${draftStatus}`, {
              date: formatDateMonthYear(locale, listing.draftStatusDate),
            })}
          </Paragraph>
        )}
      </Stack>
    );

    const daysOnMarket = (
      <Paragraph variant="p1">
        {listingState === "active" ? (
          t("publishedListingsTable.days", {
            numDays: getNumOfDaysFromDate(listing.activeDateTimeUtc)
          })
        ) : (
          "-"
        )}
      </Paragraph>
    );

    const subscriptionExpiryDateCell = (
      <Paragraph variant="p1">
        {formatDateMonthYear(locale, subscriptionRenewalDate) || "-"}
      </Paragraph>
    );

    const quickActions = (
      <Stack direction="row" gap={16}>
        {isEditable && (
          <Button
            variant="inline"
            disabled={isRedirectingToDraft}
            onClick={() => checkDraftAndRedirect(listing)}
            showNotification={draftStatus === "draftDeclined"}
            title={t("shared.edit")}
          >
            <Icon icon="pencil" />
          </Button>
        )}
        {!isCurveballListing && (
          <Button
            variant="inline"
            onClick={() => setListingLink(listing.publicListingUrl)}
            disabled={isRedirectingToDraft}
            title={t("shared.share")}
          >
            <Icon icon={"share"} />
          </Button>
        )}
        <Button
          variant="inline"
          onClick={() => router.push(`/listings/${listing.id}`)}
          disabled={isRedirectingToDraft}
          title={t("shared.preview")}
        >
          <Icon icon={"eye"} />
        </Button>
        {isStandardListing && (
          <Button
            variant="inline"
            onClick={() => {
              const url = new URL(`${process.env.NEXT_PUBLIC_AUTH_URL}/${locale}${APP_ROUTES.APPOINTMENTS}`);
              url.searchParams.set("listingId", listing.id.toString());
              location.href = url.href;
            }}
            disabled={isRedirectingToDraft}
            title={t("appointments")}
          >
            <Icon icon="calendar" />
          </Button>
        )}
      </Stack>
    );

    return {
      id: listing.id,
      items: [
        address,
        listPrice,
        status,
        startDate,
        daysOnMarket,
        subscriptionExpiryDateCell,
        quickActions
      ]
    }
  });

  return (
    <>
      <Table
        headers={[
          t("common.address"),
          t("common.listPrice"),
          t("common.status"),
          t("publishedListingsTable.startDate"),
          t("publishedListingsTable.daysOnMarket"),
          t("publishedListingsTable.subscriptionRenewalDate"),
          t("publishedListingsTable.publicListing"),
        ]}
        rows={rows}
      />
      {draftError && (
        <Toast
          close={() => setDraftError(false)}
          variant={"critical"}
          timeout={4000}
          alertIcon
        >
          {t("common.draftFailed")}
        </Toast>
      )}
    </>
  )
}
